import { useEffect, useRef } from 'react';
import { useRoomSocket } from './useRoomSocket';
import { CommandAckMessage } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useRoomCommandAck');

interface UseRoomCommandAckParams {
  commandId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  onTimeout?: () => void;
  timeout?: number; // Timeout in milliseconds, defaults to 10000ms (10 seconds)
}

/**
 * Hook to listen for command acknowledgments from the room socket server.
 * Automatically cleans up listeners on unmount or when ack is received.
 *
 * @param commandId - The unique ID of the command to listen for
 * @param onSuccess - Callback to call when command is acknowledged successfully
 * @param onError - Callback to call when command is rejected with an error
 * @param onTimeout - Optional callback to call when timeout is reached without receiving ack
 * @param timeout - Optional timeout in milliseconds (default: 10000ms)
 *
 * @example
 * const commandId = generateCommandId();
 * useRoomCommandAck({
 *   commandId,
 *   onSuccess: () => console.log('Command succeeded'),
 *   onError: (error) => console.error('Command failed:', error),
 *   onTimeout: () => console.warn('Command timed out'),
 * });
 */
export function useRoomCommandAck({
  commandId,
  onSuccess,
  onError,
  onTimeout,
  timeout = 10000,
}: UseRoomCommandAckParams) {
  const { wsClient } = useRoomSocket();
  const cleanedUpRef = useRef(false);

  useEffect(() => {
    // Don't set up listeners or timeout if commandId is empty/falsy
    // This prevents spurious timeouts when no command has been sent
    if (!commandId) {
      return;
    }

    // Reset cleanup flag for new commandId
    cleanedUpRef.current = false;

    logger.debug(`Setting up command ack listener for command ${commandId}`);

    // Set up timeout
    const timeoutId = setTimeout(() => {
      if (cleanedUpRef.current) return;

      logger.warn(
        `Command acknowledgment timeout for command ${commandId} (${timeout}ms)`
      );
      cleanedUpRef.current = true;

      if (onTimeout) {
        onTimeout();
      } else {
        // If no onTimeout provided, treat as error
        onError('Request timed out. Please try again.');
      }
    }, timeout);

    // Set up command ack listener
    const handleCommandAck = (payload: CommandAckMessage['payload']) => {
      // Only process acks for our specific command
      if (payload.commandId !== commandId) {
        return;
      }

      // Already cleaned up, ignore
      if (cleanedUpRef.current) {
        return;
      }

      logger.debug(
        `Received command ack for ${commandId}: ${payload.success ? 'success' : 'failure'}`
      );

      // Mark as cleaned up to prevent duplicate callbacks
      cleanedUpRef.current = true;

      // Clear timeout since we received the ack
      clearTimeout(timeoutId);

      // Call appropriate callback
      if (payload.success) {
        onSuccess();
      } else {
        const errorMessage = payload.error || 'Command failed';
        logger.warn(`Command ${commandId} failed: ${errorMessage}`);
        onError(errorMessage);
      }
    };

    const unsubscribe = wsClient.on('commandAck', handleCommandAck);

    // Cleanup on unmount or when commandId changes
    return () => {
      logger.debug(`Cleaning up command ack listener for command ${commandId}`);
      clearTimeout(timeoutId);
      unsubscribe();
      cleanedUpRef.current = true;
    };
  }, [commandId, onSuccess, onError, onTimeout, timeout, wsClient]);
}
